export enum InsuranceTypeApi {
  STATUTORY = 'STATUTORY',
  PRIVATE = 'PRIVATE'
}

export enum SkillLevelApi {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED'
}

export enum CefrLevelApi {
  A1 = 'A1',
  A2 = 'A2',
  B1 = 'B1',
  B2 = 'B2',
  C1 = 'C1',
  C2 = 'C2'
}

export interface PersonalDetailsRequest {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  zip: string;
  insuranceType: InsuranceTypeApi;
  insuranceName: string;
  weeklyAvailableHours: number;
  stipendBafoeg: boolean;
}

export interface EducationEntryRequest {
  university?: string;
  study?: string;
  degree?: string;
  currentlyEnrolled?: boolean;
  studyStart?: string;
  studyEnd?: string;
}

export interface ExperienceEntryRequest {
  companyName?: string;
  positionTitle?: string;
  startDate?: string;
  endDate?: string;
  currentlyWorking?: boolean;
  description?: string;
}

export interface HardSkillEntryRequest {
  name: string;
  level: SkillLevelApi;
  professionallyUsed?: boolean;
}

export interface LanguageSkillEntryRequest {
  language: string;
  level: CefrLevelApi;
}

export interface CreateCvRequest {
  language: string;
  personalDetails: PersonalDetailsRequest;
  education: EducationEntryRequest[];
  professionalExperience: ExperienceEntryRequest[];
  hardSkills: HardSkillEntryRequest[];
  softSkills: string[];
  languageSkills: LanguageSkillEntryRequest[];
  certificates: string[];
}

/**
 * Server READ/redeem shape for a CV. Unlike `CreateCvRequest` (the strict create/submit
 * contract, kept required) the response is LENIENT: the backend's Jackson `non_empty`
 * policy omits empty collections (and an all-empty personalDetails) from the JSON, so on
 * the wire any of these can be `undefined` for a partial draft — or a complete CV with no
 * optional experience/skills. Modelled here so consumers (`fromCvDto`) must defend; the
 * create/submit contract stays strict.
 */
export interface CvDto
  extends Omit<
    CreateCvRequest,
    | 'personalDetails'
    | 'education'
    | 'professionalExperience'
    | 'hardSkills'
    | 'softSkills'
    | 'languageSkills'
    | 'certificates'
  > {
  refId: string;
  createdAt?: string;
  updatedAt?: string;
  personalDetails?: PersonalDetailsRequest;
  education?: EducationEntryRequest[];
  professionalExperience?: ExperienceEntryRequest[];
  hardSkills?: HardSkillEntryRequest[];
  softSkills?: string[];
  languageSkills?: LanguageSkillEntryRequest[];
  certificates?: string[];
}

export type CreateCvResponse = { data: string };

export type GetCvResponse = { data: CvDto };
